import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, TrendingUp, Target, Award, Flame, Calendar, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import analyticsService from '@/utils/analyticsService';
import Confetti from 'react-confetti';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StepTracker = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyDays, setHistoryDays] = useState(7);
  const [customSteps, setCustomSteps] = useState('');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState('10000');
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (user) {
      fetchProgress();
      fetchHistory(7);
      
      // Check if user needs to set goal
      if (!user.daily_step_goal) {
        setShowGoalModal(true);
      }
    }
  }, [user]);

  const fetchProgress = async () => {
    try {
      const { data } = await axios.get(`${API}/steps/progress`, { withCredentials: true });
      setProgress(data);
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error('Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (days) => {
    try {
      const { data } = await axios.get(`${API}/steps/history?days=${days}`, { withCredentials: true });
      setHistory(data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleQuickAdd = async (steps) => {
    const currentSteps = progress?.today?.steps || 0;
    const newTotal = currentSteps + steps;
    await trackSteps(newTotal);
  };

  const handleCustomAdd = async () => {
    const steps = parseInt(customSteps);
    if (isNaN(steps) || steps <= 0) {
      toast.error('Please enter valid steps');
      return;
    }
    await trackSteps(steps);
    setCustomSteps('');
  };

  const trackSteps = async (steps) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const goal = user.daily_step_goal || progress?.today?.goal || 10000;
      
      const { data } = await axios.post(`${API}/steps/track`, {
        date: today,
        steps,
        goal
      }, { withCredentials: true });
      
      // Track analytics
      analyticsService.trackFeature('step_tracker_used', {
        steps_logged: steps,
        goal: goal,
        achieved: data.achieved
      });
      
      // Show celebration if goal achieved
      if (data.achieved && !progress?.today?.achieved) {
        setShowConfetti(true);
        toast.success('🎉 Amazing! You hit your goal!', {
          duration: 5000
        });
        setTimeout(() => setShowConfetti(false), 5000);
      } else if (data.achieved) {
        toast.success('Steps updated! Goal still achieved! ✅');
      } else {
        toast.success('Steps tracked successfully! 👟');
      }
      
      // Refresh data
      await fetchProgress();
      await fetchHistory(historyDays);
    } catch (error) {
      console.error('Error tracking steps:', error);
      toast.error('Failed to track steps');
    }
  };

  const handleSetGoal = async () => {
    const goal = parseInt(newGoal);
    if (isNaN(goal) || goal <= 0) {
      toast.error('Please enter a valid goal');
      return;
    }
    
    try {
      await axios.put(`${API}/steps/goal`, {
        daily_goal: goal
      }, { withCredentials: true });
      
      toast.success('Daily goal updated! 🎯');
      setShowGoalModal(false);
      
      // Refresh user data
      window.location.reload();
    } catch (error) {
      console.error('Error setting goal:', error);
      toast.error('Failed to update goal');
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-gray-400';
  };

  const getProgressBg = (percentage) => {
    if (percentage >= 100) return 'bg-green-600';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-gray-300';
  };

  const getMotivationalMessage = () => {
    const percentage = progress?.today?.percentage || 0;
    if (percentage >= 100) return "You're crushing it! 🔥";
    if (percentage >= 75) return "Almost there! Keep going! 💪";
    if (percentage >= 50) return "Halfway there! 👏";
    if (percentage >= 25) return "Great start! 🚀";
    return "Let's get moving! 👟";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4">
      {showConfetti && <Confetti recycle={false} numberOfPieces={500} />}
      
      <div className="max-w-4xl mx-auto pt-8 pb-20">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              👟 Step Tracker
            </h1>
            <Button variant="outline" onClick={() => setShowGoalModal(true)}>
              <Target className="h-4 w-4 mr-2" />
              Change Goal
            </Button>
          </div>
          <p className="text-gray-600 mt-2">{getMotivationalMessage()}</p>
        </div>

        {/* Today's Progress */}
        <Card className="mb-6 border-2 border-indigo-200">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Today's Progress</span>
              <span className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Circular Progress */}
              <div className="relative">
                <svg className="w-48 h-48">
                  <circle
                    cx="96"
                    cy="96"
                    r="80"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="12"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="80"
                    fill="none"
                    stroke={progress?.today?.achieved ? '#10b981' : '#6366f1'}
                    strokeWidth="12"
                    strokeDasharray={`${2 * Math.PI * 80}`}
                    strokeDashoffset={`${2 * Math.PI * 80 * (1 - Math.min(progress?.today?.percentage || 0, 100) / 100)}`}
                    strokeLinecap="round"
                    transform="rotate(-90 96 96)"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-4xl font-bold ${getProgressColor(progress?.today?.percentage || 0)}`}>
                    {Math.round(progress?.today?.percentage || 0)}%
                  </span>
                  <span className="text-sm text-gray-500 mt-1">
                    {progress?.today?.steps?.toLocaleString() || 0} steps
                  </span>
                </div>
              </div>

              {/* Quick Add Buttons */}
              <div className="flex-1 w-full">
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">Quick Add Steps</Label>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <Button onClick={() => handleQuickAdd(2500)} className="bg-indigo-600 hover:bg-indigo-700">
                    +2,500
                  </Button>
                  <Button onClick={() => handleQuickAdd(5000)} className="bg-indigo-600 hover:bg-indigo-700">
                    +5,000
                  </Button>
                  <Button onClick={() => handleQuickAdd(7500)} className="bg-indigo-600 hover:bg-indigo-700">
                    +7,500
                  </Button>
                  <Button onClick={() => handleQuickAdd(10000)} className="bg-indigo-600 hover:bg-indigo-700">
                    +10,000
                  </Button>
                </div>

                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Custom Steps</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Enter steps"
                    value={customSteps}
                    onChange={(e) => setCustomSteps(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={handleCustomAdd} className="bg-green-600 hover:bg-green-700">
                    Add
                  </Button>
                </div>

                <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Goal:</span> {progress?.today?.goal?.toLocaleString() || 10000} steps/day
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    <span className="font-semibold">Remaining:</span> {Math.max(0, (progress?.today?.goal || 10000) - (progress?.today?.steps || 0)).toLocaleString()} steps
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Streaks & Badges */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Current Streak */}
          <Card className="border-2 border-orange-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center">
                <Flame className="h-5 w-5 mr-2 text-orange-500" />
                Current Streak
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-orange-600">
                {progress?.streaks?.current || 0}
              </div>
              <p className="text-sm text-gray-600 mt-1">days in a row</p>
            </CardContent>
          </Card>

          {/* Longest Streak */}
          <Card className="border-2 border-purple-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center">
                <Award className="h-5 w-5 mr-2 text-purple-500" />
                Best Streak
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-purple-600">
                {progress?.streaks?.longest || 0}
              </div>
              <p className="text-sm text-gray-600 mt-1">days record</p>
            </CardContent>
          </Card>

          {/* Total Achieved */}
          <Card className="border-2 border-green-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center">
                <Target className="h-5 w-5 mr-2 text-green-500" />
                Goals Hit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-green-600">
                {progress?.total_achieved || 0}
              </div>
              <p className="text-sm text-gray-600 mt-1">total days</p>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Progress */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              This Week
            </CardTitle>
            <CardDescription>
              {progress?.week?.days_achieved || 0} of 7 days achieved • {progress?.week?.percentage || 0}% of weekly goal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {progress?.week?.days?.map((day, index) => (
                <div key={index} className="text-center">
                  <div className={`
                    rounded-lg p-3 mb-2 border-2
                    ${day.achieved ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-200'}
                  `}>
                    <div className="text-xs font-semibold text-gray-600 mb-1">{day.day_name}</div>
                    <div className={`text-lg font-bold ${day.achieved ? 'text-green-600' : 'text-gray-400'}`}>
                      {day.achieved ? '✓' : '○'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {day.steps > 0 ? `${(day.steps / 1000).toFixed(1)}k` : '-'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span className="text-gray-700">Weekly Total:</span>
                <span className="font-semibold">{progress?.week?.total_steps?.toLocaleString() || 0} steps</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Badges */}
        {progress?.badges && progress.badges.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                🏅 Badges Earned
              </CardTitle>
              <CardDescription>Keep your streak going to unlock more!</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {progress.badges.map((badge, index) => (
                  <div key={index} className="text-center p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg border-2 border-yellow-300">
                    <div className="text-4xl mb-2">{badge.icon}</div>
                    <div className="font-bold text-gray-900">{badge.name}</div>
                    <div className="text-xs text-gray-600 mt-1">{badge.days} day streak</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step History Graph */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Step History
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={historyDays === 7 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setHistoryDays(7);
                    fetchHistory(7);
                  }}
                >
                  7 Days
                </Button>
                <Button
                  variant={historyDays === 30 ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setHistoryDays(30);
                    fetchHistory(30);
                  }}
                >
                  30 Days
                </Button>
              </div>
            </div>
            <CardDescription>
              Average: {history?.average_steps?.toLocaleString() || 0} steps/day • Total: {history?.total_steps?.toLocaleString() || 0} steps
            </CardDescription>
          </CardHeader>
          <CardContent>
            {history && (
              <div className="space-y-2">
                {history.history.map((day, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-16 text-xs text-gray-600">{day.day_name}</div>
                    <div className="flex-1">
                      <div className="h-8 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${day.achieved ? 'bg-green-500' : 'bg-indigo-500'} transition-all`}
                          style={{ width: `${Math.min((day.steps / (progress?.today?.goal || 10000)) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="w-20 text-right text-sm font-semibold text-gray-700">
                      {day.steps > 0 ? day.steps.toLocaleString() : '-'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Goal Setting Modal */}
      {showGoalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Set Your Daily Step Goal</CardTitle>
              <CardDescription>Choose a goal that challenges you but feels achievable</CardDescription>
            </CardHeader>
            <CardContent>
              <Label className="mb-2 block">Daily Step Goal</Label>
              <Input
                type="number"
                value={newGoal}
                onChange={(e) => setNewGoal(e.target.value)}
                placeholder="10000"
                className="mb-4"
              />
              <div className="grid grid-cols-3 gap-2 mb-4">
                <Button variant="outline" onClick={() => setNewGoal('5000')}>5,000</Button>
                <Button variant="outline" onClick={() => setNewGoal('8000')}>8,000</Button>
                <Button variant="outline" onClick={() => setNewGoal('10000')}>10,000</Button>
              </div>
              <p className="text-xs text-gray-600 mb-4">
                💡 WHO recommends 10,000 steps per day for adults. Start with what feels right for you!
              </p>
              <div className="flex gap-2">
                <Button onClick={handleSetGoal} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                  Set Goal
                </Button>
                {user?.daily_step_goal && (
                  <Button variant="outline" onClick={() => setShowGoalModal(false)}>
                    Cancel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default StepTracker;
